import React, { useEffect, useRef, useState } from 'react';
import useAdminAccess from '../../../context/useAdminAccess';
import { errorMessage, toastError, toastSuccess } from '../../../lib/toast';
import { addItAppToCatalog, listToolCategories, searchToolOptions } from './itAppsApi';
import './itAppCatalog.css';

const DEBOUNCE_MS = 250;
const DUPLICATE_LINK = 'Another tool already uses this link.';

/** Inline errors for the "Add to catalog" mini form: `{ field: message }`, empty when valid. */
export function catalogDraftErrors({ name, link }) {
  const errors = {};
  if (!(name || '').trim()) errors.name = 'Add a name.';
  const l = (link || '').trim();
  if (!l) errors.link = 'Add a link.';
  else {
    let ok = false;
    try { ok = new URL(l).protocol === 'https:'; } catch { ok = false; }
    if (!ok) errors.link = 'Use a link that starts with https://.';
  }
  return errors;
}

// Only https links are clickable; anything else stays text.
const safeHref = (link) => {
  try { return new URL(link).protocol === 'https:' ? link : null; } catch { return null; }
};

function ToolSummary({ tool }) {
  const href = tool.link ? safeHref(tool.link) : null;
  return (
    <span className="itc-tool">
      <span className="itc-tool-name">{tool.name}</span>
      {tool.link && (href
        ? <a className="itc-tool-link" href={href} target="_blank" rel="noopener noreferrer">{tool.link}</a>
        : <span className="itc-tool-link">{tool.link}</span>)}
    </span>
  );
}

/** Debounced search over catalog tools; the parent picks with `onPick(tool)`. */
function ToolSearch({ appId, onPick, onCancel }) {
  const [q, setQ] = useState('');
  const [state, setState] = useState({ status: 'idle', tools: [] });
  const inputRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setState({ status: 'idle', tools: [] }); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      setState((s) => ({ ...s, status: 'loading' }));
      searchToolOptions(term).then(
        (tools) => { if (mine === seq.current) setState({ status: 'ready', tools }); },
        () => { if (mine === seq.current) setState({ status: 'error', tools: [] }); },
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="itc-search">
      <label className="itc-label" htmlFor="itc-search">Search the tools catalog</label>
      <div className="itc-row">
        <input
          ref={inputRef}
          id="itc-search"
          type="search"
          className="search-input itc-input"
          value={q}
          maxLength={100}
          autoComplete="off"
          placeholder="Tool name"
          aria-describedby="itc-search-status"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
            if (e.key === 'Escape' && onCancel) { e.stopPropagation(); onCancel(); }
          }}
        />
        {onCancel && <button type="button" className="btn btn-secondary btn-small" onClick={onCancel}>Cancel</button>}
      </div>
      <p id="itc-search-status" className="itc-status" role="status" aria-busy={state.status === 'loading' || undefined}>
        {state.status === 'loading' && 'Searching…'}
        {state.status === 'error' && <span className="itc-error">Couldn&apos;t search the catalog. Try again.</span>}
        {state.status === 'ready' && state.tools.length === 0 && 'No tools match.'}
      </p>
      {state.status === 'ready' && state.tools.length > 0 && (
        <ul className="itc-results" aria-label="Catalog tools">
          {state.tools.map((t) => {
            const takenBy = t.linked_it_app_id && t.linked_it_app_id !== appId ? t.linked_it_app_name : null;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  className="itc-option"
                  disabled={!!takenBy}
                  onClick={() => onPick({ id: t.id, name: t.name, link: t.link, icon_url: t.icon_url })}
                >
                  <span className="itc-option-name">{t.name}</span>
                  {takenBy
                    ? <span className="itc-option-meta">Linked to {takenBy}</span>
                    : t.link && <span className="itc-option-meta">{t.link}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Inline "Add to catalog": name (prefilled), link, category. Calls the catalog-tool endpoint. */
function AddToCatalog({ appId, appName, onCreated, onCancel }) {
  const [draft, setDraft] = useState({ name: appName || '', link: '', category_id: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState({ status: 'loading', list: [] });
  const linkRef = useRef(null);

  useEffect(() => {
    let live = true;
    linkRef.current?.focus();
    listToolCategories().then(
      (list) => { if (live) setCategories({ status: 'ready', list }); },
      () => { if (live) setCategories({ status: 'error', list: [] }); },
    );
    return () => { live = false; };
  }, []);

  const set = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));
  const label = draft.name.trim() || appName;

  const submit = async () => {
    if (busy) return;
    const local = catalogDraftErrors(draft);
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const body = {
        name: draft.name.trim(),
        link: draft.link.trim(),
        category_id: draft.category_id ? Number(draft.category_id) : null,
      };
      const { app } = await addItAppToCatalog(appId, body);
      toastSuccess(`Added ${body.name} to the tools catalog.`);
      onCreated(app);
    } catch (err) {
      if (errorMessage(err, '', { duplicate_link: DUPLICATE_LINK }) === DUPLICATE_LINK) setErrors({ link: DUPLICATE_LINK });
      else toastError(`Couldn't add ${label} to the catalog. Try again.`);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
    if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
  };

  const err = (field) => errors[field] && (
    <p id={`itc-${field}-err`} className="itc-error" role="alert">{errors[field]}</p>
  );
  const a11y = (field) => ({
    'aria-invalid': errors[field] ? true : undefined,
    'aria-describedby': errors[field] ? `itc-${field}-err` : undefined,
  });

  return (
    <fieldset className="itc-add" aria-label="Add to the tools catalog" onKeyDown={onKeyDown}>
      <div className="form-group itc-add-field">
        <label htmlFor="itc-name">Tool name</label>
        <input id="itc-name" type="text" maxLength={100} value={draft.name} onChange={set('name')} {...a11y('name')} />
        {err('name')}
      </div>
      <div className="form-group itc-add-field">
        <label htmlFor="itc-link">Link</label>
        <input
          ref={linkRef}
          id="itc-link"
          type="url"
          inputMode="url"
          placeholder="https://"
          maxLength={2000}
          required
          value={draft.link}
          onChange={set('link')}
          {...a11y('link')}
        />
        {err('link')}
      </div>
      <div className="form-group itc-add-field">
        <label htmlFor="itc-category">Category</label>
        <select id="itc-category" value={draft.category_id} onChange={set('category_id')} aria-describedby={categories.status === 'error' ? 'itc-cat-err' : undefined}>
          <option value="">No category</option>
          {categories.list.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        {categories.status === 'error' && (
          <p id="itc-cat-err" className="itc-hint">Couldn&apos;t load categories. You can add one later in Tools.</p>
        )}
      </div>
      <div className="itc-row itc-add-actions">
        <button type="button" className="btn btn-primary btn-small" disabled={busy} aria-busy={busy || undefined} onClick={submit}>
          {busy ? 'Adding…' : 'Add to catalog'}
        </button>
        <button type="button" className="btn btn-secondary btn-small" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </fieldset>
  );
}

/**
 * The IT app's link to the employee tools catalog (drawer field).
 * Picking or unlinking calls `onSelect(tool | null)`; the drawer's Save persists it.
 * "Add to catalog" writes at once (new tool + link) and calls `onCreated(savedApp)`.
 */
export default function ItAppCatalogField({ appId, appName, tool, canManage, onSelect, onCreated }) {
  const { can } = useAdminAccess();
  const [mode, setMode] = useState('view'); // view | search | add
  const canAdd = !!appId && can('tools.manage');

  if (!canManage) {
    return (
      <div className="itc">
        <span className="itc-label">Catalog tool</span>
        {tool ? <ToolSummary tool={tool} /> : <span className="itc-empty">Not in the tools catalog</span>}
      </div>
    );
  }

  const pick = (t) => { onSelect(t); setMode('view'); };

  if (tool && mode !== 'search') {
    return (
      <div className="itc">
        <span className="itc-label">Catalog tool</span>
        <div className="itc-linked">
          <ToolSummary tool={tool} />
          <div className="itc-row">
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setMode('search')}>Change</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => onSelect(null)}>Unlink</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="itc">
      {!tool && <span className="itc-label">Catalog tool</span>}
      {mode === 'add'
        ? (
          <AddToCatalog
            appId={appId}
            appName={appName}
            onCancel={() => setMode('view')}
            onCreated={(app) => { setMode('view'); onCreated(app); }}
          />
        )
        : (
          <>
            {!tool && <p className="itc-empty">Not in the tools catalog</p>}
            <ToolSearch appId={appId} onPick={pick} onCancel={tool ? () => setMode('view') : null} />
            {canAdd && !tool && (
              <button type="button" className="btn btn-secondary btn-small itc-add-btn" onClick={() => setMode('add')}>
                Add to catalog
              </button>
            )}
          </>
        )}
    </div>
  );
}
